/**
 * International Standard Atmosphere, density altitude, and the wind/gust/turbulence model.
 *
 * All SI: metres, kelvin, pascals, kg/m³, m/s. Aviation units appear only at the UI boundary.
 *
 * The ISA is a stack of layers, each either a constant lapse rate or isothermal. Layer bases
 * are computed once at module load by walking up from sea level, so evaluating the atmosphere
 * at altitude is a table lookup plus one power or exponential — cheap enough to call several
 * times per physics tick.
 *
 * A non-standard day is expressed as an offset from ISA temperature (`temperatureOffsetK`) and
 * a sea-level pressure setting (`seaLevelPressurePa`, i.e. QNH). Both matter: a hot high-altitude
 * day is exactly how a trainer runs out of climb performance, and the model must reproduce that
 * rather than pretending every day is 15 °C.
 *
 * `updateFixed(dt)`: {@link WindModel} only — gusts and turbulence are integrated at 120 Hz.
 */

import type { Rng } from '../core/rng.js';
import { clamp, expDamp } from '../core/math.js';
import { Vector3 } from 'three';

/** Sea-level ISA temperature [K]. */
export const ISA_SEA_LEVEL_TEMPERATURE_K = 288.15;
/** Sea-level ISA pressure [Pa]. */
export const ISA_SEA_LEVEL_PRESSURE_PA = 101_325;
/** Sea-level ISA density [kg/m³]. */
export const ISA_SEA_LEVEL_DENSITY = 1.225;
/** Specific gas constant for dry air [J/(kg·K)]. */
export const GAS_CONSTANT_AIR = 287.052_87;
/** Ratio of specific heats for air [-]. */
export const GAMMA_AIR = 1.4;
/** Standard gravity [m/s²]. */
export const GRAVITY = 9.806_65;

interface IsaLayer {
  /** Geopotential altitude at the base of the layer [m]. */
  baseAltitudeM: number;
  /** Temperature at the base [K]. */
  baseTemperatureK: number;
  /** Pressure at the base [Pa]. */
  basePressurePa: number;
  /** Lapse rate [K/m]; 0 means isothermal. */
  lapseRateKPerM: number;
}

/** ISA layer definitions up to 47 km — well past anything the fighter will reach. */
const LAYER_SPECS: ReadonlyArray<{ baseAltitudeM: number; lapseRateKPerM: number }> = [
  { baseAltitudeM: 0, lapseRateKPerM: -0.0065 },
  { baseAltitudeM: 11_000, lapseRateKPerM: 0 },
  { baseAltitudeM: 20_000, lapseRateKPerM: 0.001 },
  { baseAltitudeM: 32_000, lapseRateKPerM: 0.0028 },
  { baseAltitudeM: 47_000, lapseRateKPerM: 0 },
];

/** Layer bases, integrated upward from sea level once at load. */
const LAYERS: IsaLayer[] = (() => {
  const layers: IsaLayer[] = [];
  let temperature = ISA_SEA_LEVEL_TEMPERATURE_K;
  let pressure = ISA_SEA_LEVEL_PRESSURE_PA;

  for (let i = 0; i < LAYER_SPECS.length; i++) {
    const spec = LAYER_SPECS[i];
    layers.push({
      baseAltitudeM: spec.baseAltitudeM,
      baseTemperatureK: temperature,
      basePressurePa: pressure,
      lapseRateKPerM: spec.lapseRateKPerM,
    });

    const next = LAYER_SPECS[i + 1];
    if (next === undefined) break;
    const dh = next.baseAltitudeM - spec.baseAltitudeM;
    if (spec.lapseRateKPerM === 0) {
      pressure *= Math.exp((-GRAVITY * dh) / (GAS_CONSTANT_AIR * temperature));
    } else {
      const topTemperature = temperature + spec.lapseRateKPerM * dh;
      pressure *= Math.pow(
        topTemperature / temperature,
        -GRAVITY / (spec.lapseRateKPerM * GAS_CONSTANT_AIR),
      );
      temperature = topTemperature;
    }
  }
  return layers;
})();

function layerAt(altitudeM: number): IsaLayer {
  let layer = LAYERS[0];
  for (let i = 1; i < LAYERS.length; i++) {
    if (altitudeM < LAYERS[i].baseAltitudeM) break;
    layer = LAYERS[i];
  }
  return layer;
}

/** Standard-day temperature at `altitudeM` [K], ignoring any ISA offset. */
export function isaTemperatureK(altitudeM: number): number {
  const layer = layerAt(altitudeM);
  return layer.baseTemperatureK + layer.lapseRateKPerM * (altitudeM - layer.baseAltitudeM);
}

/** Standard-day pressure at `altitudeM` [Pa] for the default sea-level pressure. */
export function isaPressurePa(altitudeM: number): number {
  const layer = layerAt(altitudeM);
  const dh = altitudeM - layer.baseAltitudeM;
  if (layer.lapseRateKPerM === 0) {
    return layer.basePressurePa * Math.exp((-GRAVITY * dh) / (GAS_CONSTANT_AIR * layer.baseTemperatureK));
  }
  const temperature = layer.baseTemperatureK + layer.lapseRateKPerM * dh;
  return (
    layer.basePressurePa *
    Math.pow(temperature / layer.baseTemperatureK, -GRAVITY / (layer.lapseRateKPerM * GAS_CONSTANT_AIR))
  );
}

/** A resolved atmospheric state. Reused — never retain one across ticks. */
export interface AtmosphereSample {
  /** Static temperature [K]. */
  temperatureK: number;
  /** Static pressure [Pa]. */
  pressurePa: number;
  /** Density [kg/m³]. */
  densityKgPerM3: number;
  /** Density relative to sea-level ISA, σ [-]. Drives engine power and IAS↔TAS. */
  densityRatio: number;
  /** Speed of sound [m/s]. */
  speedOfSoundMps: number;
}

export function makeAtmosphereSample(): AtmosphereSample {
  return {
    temperatureK: ISA_SEA_LEVEL_TEMPERATURE_K,
    pressurePa: ISA_SEA_LEVEL_PRESSURE_PA,
    densityKgPerM3: ISA_SEA_LEVEL_DENSITY,
    densityRatio: 1,
    speedOfSoundMps: Math.sqrt(GAMMA_AIR * GAS_CONSTANT_AIR * ISA_SEA_LEVEL_TEMPERATURE_K),
  };
}

export interface AtmosphereOptions {
  /** Deviation from ISA temperature [K]. +20 is a hot day. */
  temperatureOffsetK?: number;
  /** Sea-level pressure setting, QNH [Pa]. */
  seaLevelPressurePa?: number;
}

/**
 * The atmosphere the simulation flies in.
 *
 * `updateFixed` / `updateFrame`: neither — it is a pure function of altitude. Sampling is
 * allocation-free: results are written into a caller-owned {@link AtmosphereSample}.
 */
export class Atmosphere {
  temperatureOffsetK: number;
  seaLevelPressurePa: number;

  constructor(options: AtmosphereOptions = {}) {
    this.temperatureOffsetK = options.temperatureOffsetK ?? 0;
    this.seaLevelPressurePa = options.seaLevelPressurePa ?? ISA_SEA_LEVEL_PRESSURE_PA;
  }

  /** Resolve the atmosphere at `altitudeM` above mean sea level, writing into `out`. */
  sample(altitudeM: number, out: AtmosphereSample): AtmosphereSample {
    const h = altitudeM < -1_000 ? -1_000 : altitudeM;
    const temperature = isaTemperatureK(h) + this.temperatureOffsetK;
    // The QNH setting scales the whole pressure column; temperature offset does not change
    // pressure at altitude, only density (that is precisely why a hot day is a high density
    // altitude at unchanged indicated altitude).
    const pressure = isaPressurePa(h) * (this.seaLevelPressurePa / ISA_SEA_LEVEL_PRESSURE_PA);
    const density = pressure / (GAS_CONSTANT_AIR * temperature);

    out.temperatureK = temperature;
    out.pressurePa = pressure;
    out.densityKgPerM3 = density;
    out.densityRatio = density / ISA_SEA_LEVEL_DENSITY;
    out.speedOfSoundMps = Math.sqrt(GAMMA_AIR * GAS_CONSTANT_AIR * temperature);
    return out;
  }

  /**
   * Density altitude [m]: the standard-day altitude with the same density as here. This is the
   * altitude the aeroplane's performance actually experiences, and on a hot day at a high
   * airfield it can be thousands of feet above the field elevation.
   */
  densityAltitudeM(altitudeM: number): number {
    const sample = this.sample(altitudeM, scratchSample);
    return densityToStandardAltitudeM(sample.densityKgPerM3);
  }
}

const scratchSample = /* #__PURE__ */ makeAtmosphereSample();

/**
 * Invert the standard atmosphere: the ISA altitude at which density equals `densityKgPerM3`.
 * Solved analytically inside the troposphere and by bisection above it.
 */
export function densityToStandardAltitudeM(densityKgPerM3: number): number {
  const layer = LAYERS[0];
  const baseDensity = layer.basePressurePa / (GAS_CONSTANT_AIR * layer.baseTemperatureK);
  const exponent = -GRAVITY / (layer.lapseRateKPerM * GAS_CONSTANT_AIR) - 1;
  const temperatureRatio = Math.pow(densityKgPerM3 / baseDensity, 1 / exponent);
  const altitude = ((temperatureRatio - 1) * layer.baseTemperatureK) / layer.lapseRateKPerM;

  if (altitude <= 11_000) return altitude;

  // Above the tropopause the closed form no longer applies; bisect instead.
  let low = 11_000;
  let high = 47_000;
  for (let i = 0; i < 60; i++) {
    const mid = (low + high) * 0.5;
    const midDensity = isaPressurePa(mid) / (GAS_CONSTANT_AIR * isaTemperatureK(mid));
    if (midDensity > densityKgPerM3) low = mid;
    else high = mid;
  }
  return (low + high) * 0.5;
}

/**
 * Equivalent airspeed for a true airspeed [m/s].
 *
 * The simulator reports this as "indicated" airspeed. Below about 200 kt and 10 000 ft the
 * compressibility correction between EAS and CAS is under a knot, so for the trainer this is
 * exact enough; the fighter milestone adds the compressible form.
 */
export function trueToIndicatedAirspeed(trueAirspeedMps: number, densityRatio: number): number {
  return trueAirspeedMps * Math.sqrt(densityRatio < 0 ? 0 : densityRatio);
}

/** True airspeed [m/s] for an indicated (equivalent) airspeed. */
export function indicatedToTrueAirspeed(indicatedAirspeedMps: number, densityRatio: number): number {
  return indicatedAirspeedMps / Math.sqrt(densityRatio <= 1e-6 ? 1e-6 : densityRatio);
}

// ---------------------------------------------------------------------------------------
// Wind
// ---------------------------------------------------------------------------------------

/** One entry in the layered wind profile. */
export interface WindLayerSpec {
  /** Altitude of the layer [m MSL]. */
  altitudeM: number;
  /** Direction the wind blows *from* [deg true] — the aviation convention. */
  directionDeg: number;
  /** Wind speed [m/s]. */
  speedMps: number;
}

export interface WindOptions {
  /** Layers, in any order; sorted on construction. */
  layers?: WindLayerSpec[];
  /** Gust standard deviation [m/s]. Gusts are slow, coherent wind changes. */
  gustSigmaMps?: number;
  /** Gust correlation time [s]. */
  gustTimeConstantS?: number;
  /** Turbulence standard deviation [m/s]. Faster and rougher than gusts. */
  turbulenceSigmaMps?: number;
  /** Turbulence correlation time [s]. */
  turbulenceTimeConstantS?: number;
}

/**
 * Layered wind with optional gusts and light turbulence.
 *
 * The steady component is linearly interpolated between layers. Gusts and turbulence are
 * Ornstein–Uhlenbeck processes — exponentially correlated noise — which gives a realistic
 * "wanders and comes back" character rather than the twitchy white noise you get from adding
 * a fresh random number every tick. Both are driven from the shared seeded RNG, so a session
 * replays identically (non-negotiable #7).
 *
 * `updateFixed(dt)`: yes, at 120 Hz, to integrate the noise processes.
 */
export class WindModel {
  private readonly layers: WindLayerSpec[];
  private readonly rng: Rng;

  private readonly gust = new Vector3();
  private readonly turbulence = new Vector3();

  gustSigmaMps: number;
  gustTimeConstantS: number;
  turbulenceSigmaMps: number;
  turbulenceTimeConstantS: number;

  constructor(rng: Rng, options: WindOptions = {}) {
    this.rng = rng;
    this.layers = [...(options.layers ?? [])].sort((a, b) => a.altitudeM - b.altitudeM);
    this.gustSigmaMps = options.gustSigmaMps ?? 0;
    this.gustTimeConstantS = options.gustTimeConstantS ?? 6;
    this.turbulenceSigmaMps = options.turbulenceSigmaMps ?? 0;
    this.turbulenceTimeConstantS = options.turbulenceTimeConstantS ?? 0.7;
  }

  /** Replace the steady wind profile. */
  setLayers(layers: WindLayerSpec[]): void {
    this.layers.length = 0;
    for (const layer of layers) this.layers.push(layer);
    this.layers.sort((a, b) => a.altitudeM - b.altitudeM);
  }

  /** Set a single uniform wind. `directionDeg` is the direction it blows *from*. */
  setSteadyWind(directionDeg: number, speedMps: number): void {
    this.setLayers([{ altitudeM: 0, directionDeg, speedMps }]);
  }

  /** Advance the gust and turbulence processes. Allocation-free. */
  updateFixed(dtSeconds: number): void {
    this.integrateOu(this.gust, this.gustSigmaMps, this.gustTimeConstantS, dtSeconds);
    this.integrateOu(
      this.turbulence,
      this.turbulenceSigmaMps,
      this.turbulenceTimeConstantS,
      dtSeconds,
    );
  }

  /**
   * One step of a three-axis Ornstein–Uhlenbeck process. The drift pulls the state back to
   * zero with the given time constant, and the diffusion term is scaled so the stationary
   * standard deviation is exactly `sigma` regardless of the step size.
   */
  private integrateOu(state: Vector3, sigma: number, timeConstantS: number, dtSeconds: number): void {
    if (sigma <= 0) {
      state.set(0, 0, 0);
      return;
    }
    const decay = Math.exp(-dtSeconds / Math.max(1e-3, timeConstantS));
    const diffusion = sigma * Math.sqrt(1 - decay * decay);
    state.x = state.x * decay + this.rng.gaussian(0, 1) * diffusion;
    state.y = state.y * decay + this.rng.gaussian(0, 1) * diffusion;
    state.z = state.z * decay + this.rng.gaussian(0, 1) * diffusion;
  }

  /**
   * Total wind velocity at `altitudeM`, in **NED** [m/s] — the velocity of the air mass, so a
   * wind *from* the north is `(−speed, 0, 0)`. Writes into `out`; allocation-free.
   */
  sample(altitudeM: number, out: Vector3): Vector3 {
    this.sampleSteady(altitudeM, out);
    out.x += this.gust.x + this.turbulence.x;
    out.y += this.gust.y + this.turbulence.y;
    // Vertical gusts are roughly half the horizontal intensity near the ground.
    out.z += (this.gust.z + this.turbulence.z) * 0.5;
    return out;
  }

  /** The steady layered component only, without gusts. */
  sampleSteady(altitudeM: number, out: Vector3): Vector3 {
    const layers = this.layers;
    if (layers.length === 0) return out.set(0, 0, 0);

    let directionDeg: number;
    let speedMps: number;

    if (altitudeM <= layers[0].altitudeM) {
      directionDeg = layers[0].directionDeg;
      speedMps = layers[0].speedMps;
    } else if (altitudeM >= layers[layers.length - 1].altitudeM) {
      directionDeg = layers[layers.length - 1].directionDeg;
      speedMps = layers[layers.length - 1].speedMps;
    } else {
      let upper = 1;
      while (upper < layers.length && layers[upper].altitudeM < altitudeM) upper++;
      const a = layers[upper - 1];
      const b = layers[upper];
      const t = clamp((altitudeM - a.altitudeM) / (b.altitudeM - a.altitudeM), 0, 1);
      speedMps = a.speedMps + (b.speedMps - a.speedMps) * t;
      // Interpolate direction the short way round so 350° → 010° passes through north.
      const delta = ((b.directionDeg - a.directionDeg + 540) % 360) - 180;
      directionDeg = a.directionDeg + delta * t;
    }

    // "From" direction to a velocity vector: wind from 000° moves the air toward the south.
    const fromRad = (directionDeg * Math.PI) / 180;
    return out.set(-Math.cos(fromRad) * speedMps, -Math.sin(fromRad) * speedMps, 0);
  }

  /** Current gust vector [m/s, NED] — exposed for the debug HUD. */
  get gustVector(): Vector3 {
    return this.gust;
  }

  /** Smoothly relax the gust state toward zero, e.g. when the player disables weather. */
  calm(dtSeconds: number): void {
    this.gust.set(
      expDamp(this.gust.x, 0, 0.5, dtSeconds),
      expDamp(this.gust.y, 0, 0.5, dtSeconds),
      expDamp(this.gust.z, 0, 0.5, dtSeconds),
    );
    this.turbulence.set(0, 0, 0);
  }
}
